import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheckIcon, UserPlusIcon, TrashIcon, KeyIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function AdminAdmins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/auth/admin/admins"); setAdmins(data.admins); }
    catch { setAdmins([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Enter a valid email");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/admin/admins", { email: form.email.trim(), password: form.password });
      toast.success(data.created ? `Admin ${data.email} added` : `Password updated for ${data.email}`);
      setForm({ email: "", password: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add admin"); }
    finally { setSaving(false); }
  };

  const remove = async (email) => {
    try {
      await api.delete(`/auth/admin/admins/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      setToRemove(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3">// Admin · access</p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Admins</h1>
        <p className="text-white/50 mt-4 max-w-2xl">Super-admins who can sign in at <span className="font-mono text-white/70">/admin/login</span> with email + password. Add or remove them here — changes take effect immediately, no redeploy needed.</p>
      </div>

      {/* Add admin */}
      <div className="border border-border bg-card p-6 mb-8">
        <div className="flex items-center gap-2 text-primary mb-4"><UserPlusIcon weight="duotone" size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">Add / update an admin</span></div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Email</label>
            <Input data-testid="admin-add-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="new.admin@email.com" className="mt-1 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Password</label>
            <Input data-testid="admin-add-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="at least 6 characters" className="mt-1 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
          </div>
          <Button data-testid="admin-add-save" onClick={add} disabled={saving} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-xs h-10">
            <KeyIcon weight="bold" className="mr-1.5" /> {saving ? "SAVING…" : "SAVE ADMIN"}
          </Button>
        </div>
        <p className="font-mono text-[10px] text-white/40 mt-3">Adding an existing email just resets that admin's password.</p>
      </div>

      {/* List */}
      <div className="border border-border" data-testid="admin-list">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-secondary/20 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          <div className="col-span-6">Admin</div>
          <div className="col-span-4">Added</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {admins === null ? (
          <p className="font-mono text-sm text-primary animate-pulse px-6 py-6">LOADING…</p>
        ) : admins.length === 0 ? (
          <p className="font-mono text-sm text-white/40 px-6 py-6">No admins found.</p>
        ) : (
          admins.map((a) => (
            <div key={a.email} data-testid={`admin-row-${a.email}`} className="grid grid-cols-12 items-center gap-4 px-6 py-4 border-b border-border last:border-0">
              <div className="col-span-6 flex items-center gap-2 min-w-0">
                <ShieldCheckIcon weight="fill" className="text-primary shrink-0" />
                <span className="font-mono text-sm truncate">{a.email}</span>
                {a.is_self && <Badge variant="outline" className="font-mono text-[9px] border-primary/40 text-primary">YOU</Badge>}
                {a.from_env && <Badge variant="outline" className="font-mono text-[9px] border-white/25 text-white/50" title="Defined in server environment">ENV</Badge>}
              </div>
              <div className="col-span-4 font-mono text-[11px] text-white/40">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</div>
              <div className="col-span-2 flex justify-end">
                <button
                  data-testid={`admin-remove-${a.email}`}
                  onClick={() => setToRemove(a.email)}
                  disabled={a.is_self}
                  title={a.is_self ? "You can't remove yourself" : "Remove admin"}
                  className="flex items-center justify-center h-8 w-8 border border-border text-white/50 hover:text-destructive hover:border-destructive/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <TrashIcon weight="bold" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="admin-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove admin access?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              <span className="font-mono text-primary">{toRemove}</span> will no longer be able to sign in to the admin area. If this admin is defined in the server environment, it will reappear on the next restart.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">CANCEL</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-remove-confirm" onClick={(e) => { e.preventDefault(); remove(toRemove); }} className="bg-destructive text-white font-bold hover:bg-destructive/90">REMOVE</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
